import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import { useForm, useWatch } from 'react-hook-form';
import {
    CheckboxElement,
    FormContainer,
    SelectElement,
    TextFieldElement,
} from 'react-hook-form-mui';
import { Card, CardContent, CardHeader, Stack } from '@mui/material';

import {
    BusinessInput,
    CustomDrinkInput,
    EditButtons,
    ProductInput,
    StandardDrinkInput,
} from '@barboxx/admin-components';
import { MENU_ITEM_SCHEMA } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { MenuItem } from '@barboxx/models';
import { Can, useBool } from '@barboxx/react-utils';
import { MENU_ITEM_TYPES } from '@barboxx/types';
import { enumToFormOptions } from '@barboxx/utils';

interface Props {
    menuItem: MenuItem;
}

export const MenuItemDetailsCard = ({ menuItem }: Props) => {
    const [editable, editableActions] = useBool(false);
    const notifications = useNotifications();

    const mutateMenuItem = useMutation({
        mutationKey: ['menuItem', menuItem.id],
        mutationFn: (data: MenuItem.UpdateRequest) =>
            Api.Menu.MenuItem.putMenuItem(menuItem.id, data),
    });

    const formContext = useForm<MenuItem.UpdateRequest>({
        resolver: yupResolver(MENU_ITEM_SCHEMA),
        defaultValues: menuItem,
    });

    const handleCancel = () => {
        editableActions.off();
        formContext.reset(undefined);
    };

    const onSubmit = async (values: MenuItem.UpdateRequest) => {
        mutateMenuItem
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show('Updated menu item!', {
                    severity: 'success',
                });

                editableActions.off();
                formContext.reset(data);
            })
            .catch(e => {
                notifications.show('Error Updating menu item!', {
                    severity: 'error',
                });
            });
    };

    const values = useWatch(formContext) as MenuItem.UpdateRequest;

    return (
        <Card>
            <CardHeader title="MenuItem Details" />
            <CardContent>
                <FormContainer onSuccess={onSubmit} formContext={formContext}>
                    <Stack gap={2}>
                        <BusinessInput disabled={!editable} />
                        <SelectElement
                            label="Type"
                            name="type"
                            options={enumToFormOptions(MENU_ITEM_TYPES)}
                            fullWidth
                            disabled={!editable}
                            onChange={() => {
                                formContext.setValue('standardDrinkId', null);
                                formContext.setValue('customDrinkId', null);
                                formContext.setValue('productId', null);
                            }}
                        />
                        {values.type === MENU_ITEM_TYPES.StandardDrink && (
                            <StandardDrinkInput disabled={!editable} />
                        )}
                        {values.type === MENU_ITEM_TYPES.CustomDrink && (
                            <CustomDrinkInput disabled={!editable} />
                        )}
                        {values.type === MENU_ITEM_TYPES.Product && (
                            <ProductInput disabled={!editable} />
                        )}
                        <TextFieldElement
                            label="displayOrder"
                            name="displayOrder"
                            fullWidth
                            disabled={!editable}
                        />
                        <TextFieldElement
                            label="Notes"
                            name="notes"
                            fullWidth
                            disabled={!editable}
                        />
                        <CheckboxElement
                            label="isActive"
                            name="isActive"
                            disabled={!editable}
                        />
                        <CheckboxElement
                            label="Featured"
                            name="featuredItem"
                            disabled={!editable}
                        />

                        <Can I="update" a="MenuItem">
                            <EditButtons
                                formContext={formContext}
                                handleCancel={handleCancel}
                                editable={editable}
                                editableActions={editableActions}
                            />
                        </Can>
                    </Stack>
                </FormContainer>
            </CardContent>
        </Card>
    );
};
